import Player from '@vimeo/player';

/**
 * The Video class works in conjunction with the video snippet. It scrapes data
 * from the snippet and instantiates a custom Vimeo player on that data.
 * It handles custom play/pause and mute/unmute buttons
 */
export default class Video {
  /**
   * @param {DOMnode} element top-level node for the section/component
   * @param {DOMnode} video top-level node for the video
   * @param {Class} layout for handling breakpoints
   */
  constructor({ element, video, layout }) {
    const dataEl = video.querySelector('[data-component-data]');
    this.layout = layout;
    this.data = dataEl ? JSON.parse(dataEl.textContent) : {};
    this.isMuted = this.data.muted;
    this.isMobile;
    this.element = element;
    this.isPlaying = this.data.autoplay;
    this.hasMultipleVideos =
      !!this.data.videoDesktop && !!this.data.videoMobile;

    this.handlePlay = this.handlePlay.bind(this);
    this.handleMute = this.handleMute.bind(this);
    this.handleBpChange = this.handleBpChange.bind(this);

    this.playEl = this.data.play.selector
      ? this.element.querySelector(this.data.play.selector)
      : video.querySelector('[data-video-play]');
    this.muteEl = this.data.mute.selector
      ? this.element.querySelector(this.data.mute.selector)
      : video.querySelector('[data-video-mute]');

    this.init();
  }

  handlePlay = () => {
    if (this.isPlaying) {
      this.player.pause();
      this.isPlaying = false;
      this.playEl?.setAttribute('aria-label', this.data.play.playText);
    } else {
      this.player.play();
      this.isPlaying = true;
      this.playEl?.setAttribute('aria-label', this.data.play.pauseText);
    }
    this.playEl &&
      [...this.playEl.children].forEach((child) =>
        child.classList.toggle('hidden'),
      );
  };

  handleMute = () => {
    if (this.isMuted) {
      this.player.setVolume(1);
      this.isMuted = false;
      this.muteEl?.setAttribute('aria-label', this.data.mute.muteText);
    } else {
      this.player.setVolume(0);
      this.isMuted = true;
      this.muteEl?.setAttribute('aria-label', this.data.mute.unmuteText);
    }
    this.muteEl &&
      [...this.muteEl.children].forEach((child) =>
        child.classList.toggle('hidden'),
      );
  };

  /**
   * Destroys the player and re-inits
   * Cleans up event listeners
   * Resets custom controls
   */
  resetPlayer() {
    // Unbind event listeners
    this.playEl?.removeEventListener('click', this.handlePlay);
    this.muteEl?.removeEventListener('click', this.handleMute);
    if (this.layout)
      window.removeEventListener('bpChange', this.handleBpChange);
    // Destroy vimeo player
    this.player.destroy().then(() => {
      // Reset play control
      this.isPlaying = this.data.autoplay;
      this.playEl?.setAttribute(
        'aria-label',
        this.data.autoplay ? this.data.play.pauseText : this.data.play.playText,
      );
      this.playEl?.children[this.data.autoplay ? 1 : 0].classList.remove(
        'hidden',
      );
      this.playEl?.children[this.data.autoplay ? 0 : 1].classList.add('hidden');
      // Reset mute control
      this.isMuted = this.data.muted;
      this.muteEl?.setAttribute('aria-label', this.data.mute.unmuteText);
      this.muteEl?.children[0].classList.remove('hidden');
      this.muteEl?.children[1].classList.add('hidden');
      // Re-initialize
      this.init();
    });
  }

  handleBpChange() {
    if (this.isMobile && this.layout.isAboveBreakpoint('sm')) {
      this.isMobile = false;
      this.resetPlayer();
    } else if (!this.isMobile && !this.layout.isAboveBreakpoint('sm')) {
      this.isMobile = true;
      this.resetPlayer();
    }
  }

  showBgImage() {
    // Show the image
    this.element.querySelector('[data-bg-image]')?.classList.remove('hidden');
    // Hide control buttons
    this.element
      .querySelector('[data-video-controls]')
      ?.classList.add('hidden');
  }

  init() {
    if (!this.data.videoDesktop && !this.data.videoMobile) this.showBgImage();

    if (this.layout && this.hasMultipleVideos) {
      if (this.layout.isAboveBreakpoint('sm')) {
        this.initPlayer(this.data.videoDesktop);
        this.isMobile = false;
      } else {
        this.initPlayer(this.data.videoMobile);
        this.isMobile = true;
      }
    } else if (!!this.data.videoDesktop) {
      this.initPlayer(this.data.videoDesktop);
    } else if (!!this.data.videoMobile) {
      this.initPlayer(this.data.videoMobile);
    }
  }

  initPlayer(id) {
    this.player = new Player(`video-${id}`, {
      id,
      controls: false,
      autoplay: this.data.autoplay,
      muted: this.data.muted,
      responsive: !this.data.disableResponsive,
      autopause: false,
      loop: true,
    });

    this.player
      .ready()
      .then(() => {
        this.playEl?.addEventListener('click', this.handlePlay);
        this.muteEl?.addEventListener('click', this.handleMute);
        if (this.layout && this.hasMultipleVideos)
          window.addEventListener('bpChange', this.handleBpChange);
      })
      .catch(() => {
        this.showBgImage();
      });
  }
}
